import React, { useState } from 'react';
import {
  Button,
  Input,
  Modal,
  notification,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import {
  downloadPackageLabel,
  MassiveItemState,
} from 'services/MassiveService';
import { MassiveApiError } from 'services/MassiveErrors';
import {
  PACKAGES_PAGE_SIZE,
  useCancelMassivePackage,
  useMassivePackages,
} from 'queries/useMassiveQuery';
import { downloadOrNotify } from './download';
import MassiveServiceError, { notifyMassiveError } from './serviceError';

interface Props {
  open: boolean;
  massiveId?: string;
  onClose: () => void;
  onViewPackage?: (daliInternalId: number) => void;
}

const creationTag = (item: MassiveItemState) => {
  if (item.creation_status === 'created') {
    return <Tag color="success">Creado</Tag>;
  }
  if (item.creation_status === 'cancelled') {
    return <Tag>Eliminado</Tag>;
  }
  if (item.creation_status === 'creation_failed') {
    return (
      <Tooltip title={item.creation_reason}>
        <Tag color="error">Falló</Tag>
      </Tooltip>
    );
  }
  return <Tag color="processing">En cola</Tag>;
};

const MassivePackagesModal = ({
  open,
  massiveId,
  onClose,
  onViewPackage,
}: Props) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const { data, isFetching, isError, error, refetch } = useMassivePackages(
    open ? massiveId : undefined,
    search,
    page,
  );
  const cancelPackage = useCancelMassivePackage();

  const remove = (item: MassiveItemState) =>
    Modal.confirm({
      title: 'Eliminar el paquete',
      okText: 'Eliminar',
      okButtonProps: { danger: true },
      cancelText: 'Volver',
      content: `Se va a eliminar el paquete ${item.tracking}. No se puede deshacer.`,
      onOk: async () => {
        try {
          await cancelPackage.mutateAsync({
            massiveId: massiveId as string,
            tracking: item.tracking,
          });
          notification.success({
            message: 'Listo',
            description: `El paquete ${item.tracking} quedó eliminado.`,
          });
        } catch (failure) {
          if (failure instanceof MassiveApiError && failure.status === 409) {
            notification.warning({
              message: 'No se puede eliminar',
              description: 'El paquete ya se empezó a operar.',
            });
            return;
          }
          notifyMassiveError(failure, 'No se pudo eliminar el paquete');
        }
      },
    });

  const columns = [
    {
      title: 'Fila',
      dataIndex: 'row',
      width: 70,
      align: 'center' as const,
    },
    {
      title: 'Nro. seguimiento',
      dataIndex: 'tracking',
      width: 220,
    },
    {
      title: 'Estado',
      key: 'status',
      width: 120,
      render: (_: unknown, item: MassiveItemState) => creationTag(item),
    },
    {
      title: 'Ver',
      key: 'view',
      width: 70,
      align: 'center' as const,
      render: (_: unknown, item: MassiveItemState) => (
        <Tooltip
          title={
            item.dali_internal_id ? 'Ver el paquete' : 'Todavía no se creó'
          }
        >
          <Button
            size="small"
            icon={<EyeOutlined />}
            disabled={!item.dali_internal_id || !onViewPackage}
            onClick={() => onViewPackage?.(item.dali_internal_id as number)}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Eliminar',
      key: 'remove',
      width: 90,
      align: 'center' as const,
      render: (_: unknown, item: MassiveItemState) => (
        <Tooltip
          title={
            item.creation_status === 'created'
              ? 'Eliminar este paquete'
              : 'Sólo se pueden eliminar los paquetes creados'
          }
        >
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={item.creation_status !== 'created'}
            onClick={() => remove(item)}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Etiqueta',
      key: 'label',
      width: 100,
      align: 'center' as const,
      render: (_: unknown, item: MassiveItemState) => (
        <Button
          size="small"
          icon={<DownloadOutlined />}
          disabled={item.creation_status !== 'created'}
          onClick={() =>
            downloadOrNotify(
              () =>
                downloadPackageLabel(massiveId as string, item.tracking, 'pdf'),
              `${item.tracking}.pdf`,
            )
          }
        />
      ),
    },
  ];

  return (
    <Modal
      open={open}
      width={860}
      title={`Paquetes de la masiva ${massiveId ?? ''}`}
      onCancel={onClose}
      onOk={onClose}
      cancelButtonProps={{ style: { display: 'none' } }}
      okText="Cerrar"
    >
      <Space style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="Buscar por número de seguimiento"
          onSearch={(value) => {
            setSearch(value);
            setPage(0);
          }}
          style={{ width: 320 }}
        />
      </Space>
      {isError ? (
        <MassiveServiceError
          error={error}
          title="No se pudieron cargar los paquetes"
          onRetry={() => refetch()}
        />
      ) : (
        <Table
          size="small"
          rowKey="row"
          loading={isFetching}
          columns={columns}
          dataSource={data?.content ?? []}
          pagination={{
            current: page + 1,
            pageSize: PACKAGES_PAGE_SIZE,
            total: data?.total ?? 0,
            showSizeChanger: false,
            onChange: (next) => setPage(next - 1),
          }}
        />
      )}
    </Modal>
  );
};

export default MassivePackagesModal;
